// Flash Cards — template-local helpers.
// Self-contained: Flash Cards owns its own card schema, id generation, copy,
// and media enumeration. It does NOT import another template's internal helpers.
// Only public utils (shortid, utils/addIdToFilename) are reused.

import shortId from 'shortid';
import { addIdToFilename } from 'utils';

export const MODE = {
  MEMORY: 'memory',
  FILL_IN: 'fill-in',
  PICK_ONE: 'pick-one',
};

export const ALL_MODES = [MODE.MEMORY, MODE.FILL_IN, MODE.PICK_ONE];

// Number of authored faces per mode.
export const facesForMode = (mode) => (mode === MODE.FILL_IN ? 1 : 2);

// A fresh card with a stable id.
export const createProblem = () => ({
  id: shortId.generate(),
  instruction: { text: '', audio: '' },
  items: [],
});

// An item carries one of text | image | audio.
export const isItemFilled = (item) =>
  !!(item && (item.text || item.image || item.audio));

// Collect every durable asset referenced by a card: both/all face assets AND
// the optional instruction audio. The shared ProblemList delete/undo lifecycle
// uses this via the `getContentFilesFromProblem` prop, so instruction audio is
// included in remove/restore. (Concentration's own collector omits instruction
// audio; we do not reuse it.)
export const getContentFilesFromProblem = (problem) => {
  const files = [];
  if (!problem) return files;
  (problem.items || []).forEach((item) => {
    if (item && item.image) files.push(item.image);
    if (item && item.audio) files.push(item.audio);
  });
  if (problem.instruction && problem.instruction.audio) {
    files.push(problem.instruction.audio);
  }
  return files;
};

// Copy a card for the shared ProblemList `copyProblem` prop.
// Contract (from ProblemList.copyProblemHandler): returns [copy, files] where
// `copy` has a NEW stable id and `files` is an array of { src, dest } rename
// sets fed to API.storage.copyFiles. Every face asset AND instruction audio is
// duplicated so the copy never shares mutable asset filenames with the origin.
export const copyProblem = (problem) => {
  const files = [];
  const remap = (filename) => {
    if (!filename) return filename;
    const dest = addIdToFilename(filename);
    files.push({ src: filename, dest });
    return dest;
  };

  const items = (problem.items || []).map((item) => {
    if (!item) return item;
    const next = { ...item };
    if (item.image) next.image = remap(item.image);
    if (item.audio) next.audio = remap(item.audio);
    return next;
  });

  let instruction = problem.instruction;
  if (instruction && instruction.audio) {
    instruction = { ...instruction, audio: remap(instruction.audio) };
  } else if (instruction) {
    instruction = { ...instruction };
  }

  const copy = {
    ...problem,
    id: shortId.generate(),
    items,
    instruction,
  };

  return [copy, files];
};

// Prepare display order for one play session (FC-09). Shuffle once when
// randomOrder is set; otherwise keep authored order. The caller keeps this
// result for the whole session and must NOT reshuffle on render/flip/retry.
export const buildSessionOrder = (problems, randomOrder) => {
  const list = Array.isArray(problems) ? [...problems] : [];
  if (!randomOrder) return list;
  // Fisher–Yates — stable permutation, no omissions/duplicates.
  for (let i = list.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [list[i], list[j]] = [list[j], list[i]];
  }
  return list;
};
